import React, { ChangeEvent, ReactNode, RefObject } from "react";
import { Col, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { FaCamera } from "react-icons/fa";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IThermogram } from "pages/thermography/model/thermogram.model";

interface ThermogramSectionProps {
    title: string;
    mode: string | null;

    thermogram: IThermogram;

    termogramUrl?: string | null;
    onOpenLightbox?: () => void;

    handleRoiChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;

    handleThermogramButtonClick?: () => void;
    thermogramInputRef?: RefObject<HTMLInputElement>;
    onThermogramChange?: (e: ChangeEvent<HTMLInputElement>) => void;

    children?: ReactNode;
}

const ThermogramSection = ({
    title,
    mode,
    thermogram,
    termogramUrl,
    onOpenLightbox,
    handleRoiChange,
    handleThermogramButtonClick,
    thermogramInputRef,
    onThermogramChange
}: ThermogramSectionProps) => {
    const { t } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
    const isView = mode === "view";
    const valueTextStyle: React.CSSProperties = {
        marginLeft: "auto",
        fontSize: "0.95rem",
        fontWeight: 500,
        marginRight: "2px"
    };

    const ValueText = ({ children }: { children: React.ReactNode }) => (
        <span className="text-muted" style={valueTextStyle}>
            {children ?? "--"}
        </span>
    );

    const selectedRoiLabel =
        thermogram.rois?.find((roi) => roi.id === thermogram.selectedRoiId)?.label ??
        thermogram.rois?.[0]?.label ??
        "--";

    const fontSize = isView ? "0.84rem" : "1rem";

    return (
        <>
            <div className="d-flex justify-content-between align-items-center">
                <span style={{ padding: "3px", fontWeight: 700, fontSize: fontSize }}>
                    {title}
                </span>
                {thermogram.rois && thermogram.rois.length > 0 && (
                    <>
                        {isView ? (
                            <ValueText>{selectedRoiLabel}</ValueText>
                        ) : (
                            <Form.Select
                                value={thermogram.selectedRoiId || ""}
                                onChange={handleRoiChange}
                                required
                                disabled={mode === "view"}
                                style={{
                                    width: "150px",
                                    marginRight: "50px",
                                    marginBottom: "0px",
                                    padding: "2px",
                                }}
                            >
                                {thermogram.rois?.map((roi) => (
                                    <option key={roi.id} value={roi.id}>
                                        {roi.label}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </>
                )}

                {!isView && (
                    <button
                        type="button"
                        className="btn btn-light btn-sm position-absolute end-0 m-0 p-2 border rounded-circle"
                        aria-label={tt("pages.thermography.thermogramSection.selectImageAria")}
                        onClick={handleThermogramButtonClick}
                        style={{ marginTop: "0px!important" }}
                    >
                        <FaCamera />
                    </button>
                )}

                <input
                    ref={thermogramInputRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={onThermogramChange}
                />
            </div>

            <Row className="flex-fill thermogram-section__container" style={{ overflow: "hidden", padding: '5px' }}>
                <Col className="d-flex align-items-center justify-content-center" style={{ height: '100%', overflow: "hidden" }}>
                    <div
                        className="border rounded d-flex justify-content-center align-items-center bg-light"
                        style={{ width: '100%', height: "100%", overflow: "hidden" }}
                    >
                        {termogramUrl ? (
                            <img
                                src={termogramUrl}
                                loading="lazy"
                                alt={tt("pages.thermography.thermogramSection.previewAlt")}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    display: 'block',
                                    cursor: 'pointer'
                                }}
                                onClick={onOpenLightbox}
                            />
                        ) : (
                            <span className="text-muted">{tt("pages.thermography.thermogramSection.noImage")}</span>
                        )}
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default ThermogramSection;
